import React, {useEffect, useRef, useState} from 'react';
import {Link, NavLink, useLocation} from 'react-router-dom';
import './Header.css';
import company from '../config/company';
import curacareLogo from '../assets/images/headerLogo.webp';
import instagramLogo from '../assets/images/InstagramLogo.webp';

const menus = [
  {id: 'leistungen', label: 'Leistungen', to: '/leistungen'},
  {id: 'pflegekosten', label: 'Pflegekosten', to: '/pflegekosten'},
  {id: 'team', label: 'Team', to: '/team'},
  {id: 'faq', label: 'FAQ', to: '/faq'},
  {id: 'galerie', label: 'Galerie', to: '/galerie'}
];

const PhoneIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2" />
  </svg>
);

const MailIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3 7l9 6 9-6" />
  </svg>
);

const Header = () => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const scrollPositionRef = useRef(0);

  // Hintergrund einfrieren, solange das mobile Menü offen ist.
  useEffect(() => {
    const body = document.body;
    const html = document.documentElement;
    if (isMobileMenuOpen) {
      scrollPositionRef.current = window.scrollY;
      body.style.position = 'fixed';
      body.style.top = `-${scrollPositionRef.current}px`;
      body.style.width = '100%';
      body.style.overflow = 'hidden';
      html.style.overflow = 'hidden';
      html.style.height = '100%';
    } else {
      const lockedScroll = parseInt(body.style.top || '0', 10) * -1;
      body.style.position = '';
      body.style.top = '';
      body.style.width = '';
      body.style.overflow = '';
      html.style.overflow = '';
      html.style.height = '';
      if (lockedScroll) window.scrollTo(0, lockedScroll);
    }
    return () => {
      body.style.position = '';
      body.style.top = '';
      body.style.width = '';
      body.style.overflow = '';
      html.style.overflow = '';
      html.style.height = '';
    };
  }, [isMobileMenuOpen]);

  // Menü bei Routenwechsel schließen.
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname, location.hash]);

  // Escape schließt das Menü.
  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setIsMobileMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isMobileMenuOpen]);

  const closeMenu = () => setIsMobileMenuOpen(false);
  const navClass = ({isActive}) => `nav-link${isActive ? ' is-active' : ''}`;

  return (
    <header className={`header${isMobileMenuOpen ? ' header--mobile-open' : ''}`}>
      {isMobileMenuOpen && (
        <button
          type="button"
          className="header-backdrop"
          aria-label="Navigation schließen"
          onClick={closeMenu}
        />
      )}

      <div className="header-topbar">
        <div className="header-topbar__inner">
          <a href={`tel:${company.phoneIntl}`}><PhoneIcon /> {company.phone}</a>
          <a href={`mailto:${company.email}`}><MailIcon /> {company.email}</a>
          <span className="header-topbar__spacer" />
          <span>{company.openingHours.label}</span>
          <span className="header-topbar__badge">24h-Rufbereitschaft</span>
        </div>
      </div>

      <div className="header-shell">
        <div className="header-bar">
          <div className="header-logo">
            <Link to="/" onClick={closeMenu} aria-label="Zur Startseite">
              <img src={curacareLogo} alt="Cura Care Pflegedienst" width="1182" height="358" />
            </Link>
          </div>

          <nav className="header-nav-desktop" aria-label="Hauptnavigation">
            <ul>
              {menus.map((menu) => (
                <li key={menu.id} className="nav-item">
                  <NavLink to={menu.to} className={navClass}>
                    {menu.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          <div className="header-bar-right">
            <a
              className="header-phone-btn"
              href={`tel:${company.phoneIntl}`}
              aria-label={`Cura Care anrufen unter ${company.phone}`}
            >
              <PhoneIcon />
            </a>

            <Link className="header-cta" to="/kontakt">Beratung anfragen</Link>

            <a
              className="header-instagram"
              href={company.instagram}
              target="_blank"
              rel="noreferrer"
              aria-label="Cura Care auf Instagram"
            >
              <img src={instagramLogo} alt="" className="header-small-logo" width="26" height="26" loading="lazy" />
            </a>

            <button
              type="button"
              className="header-menu-toggle"
              aria-label={isMobileMenuOpen ? 'Menü schließen' : 'Menü öffnen'}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-navigation"
              onClick={() => setIsMobileMenuOpen((prev) => !prev)}
            >
              <span className="header-menu-toggle__label">Menü</span>
              <span className="header-menu-toggle__icon" aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
            </button>
          </div>
        </div>
      </div>

      <nav
        id="mobile-navigation"
        className="header-nav-mobile"
        aria-label="Hauptnavigation mobil"
        inert={!isMobileMenuOpen}
      >
        <div className="header-nav-mobile__head">
          <img src={curacareLogo} alt="" className="header-nav-mobile__logo" width="1182" height="358" />
          <button
            type="button"
            className="header-nav-mobile__close"
            aria-label="Menü schließen"
            onClick={closeMenu}
          >
            ×
          </button>
        </div>

        <ul className="header-nav-mobile__list">
          {menus.map((menu) => (
            <li key={menu.id} className="header-nav-mobile__item">
              <NavLink to={menu.to} onClick={closeMenu} className={({isActive}) => (isActive ? 'is-active' : undefined)}>
                {menu.label}
              </NavLink>
            </li>
          ))}
          <li className="header-nav-mobile__item">
            <NavLink to="/kontakt" onClick={closeMenu}>Kontakt</NavLink>
          </li>
        </ul>

        <a className="header-phone-link header-phone-link--mobile" href={`tel:${company.phoneIntl}`}>
          <PhoneIcon /> {company.phone}
        </a>
        <p className="header-nav-mobile__hours">{company.openingHours.label} · 24h-Rufbereitschaft</p>

        <Link className="header-cta header-cta--mobile" to="/kontakt" onClick={closeMenu}>
          Kostenlos beraten lassen
        </Link>
      </nav>
    </header>
  );
};

export default Header;
